/**
 * Timezone Auto-Detection Service
 * Handles automatic timezone detection for new users on first login
 */

import { supabase } from '@/lib/supabase'
import { detectUserTimezone, isValidTimezone } from '@/lib/timezone-utils'
import { User } from '@supabase/supabase-js'

export interface TimezoneAutoDetectionResult {
  detected: boolean
  timezone: string | null
  wasAlreadySet: boolean
  error?: string
}

export interface TimezoneAutoDetectionOptions {
  updateProfile?: boolean
  silentMode?: boolean
}

/**
 * Auto-detect and set timezone for a user if they don't already have one set
 */
export async function autoDetectUserTimezone(
  user: User,
  options: TimezoneAutoDetectionOptions = {}
): Promise<TimezoneAutoDetectionResult> {
  const { updateProfile = true, silentMode = false } = options

  try {
    // Check if user already has a timezone set
    const existingTimezone = user.user_metadata?.timezone || user.app_metadata?.timezone

    if (existingTimezone && isValidTimezone(existingTimezone)) {
      return {
        detected: false,
        timezone: existingTimezone,
        wasAlreadySet: true
      }
    }

    // Also check the profile table for timezone
    const { data: profile } = await supabase
      .schema('marketing_and_customer_success')
      .from('profiles')
      .select('timezone')
      .eq('id', user.id)
      .single()

    if (profile?.timezone && isValidTimezone(profile.timezone)) {
      return {
        detected: false,
        timezone: profile.timezone,
        wasAlreadySet: true
      }
    }

    // Auto-detect timezone
    const detectedTimezone = detectUserTimezone()

    if (!detectedTimezone || !isValidTimezone(detectedTimezone)) {
      return {
        detected: false,
        timezone: null,
        wasAlreadySet: false,
        error: 'Unable to detect a valid timezone'
      }
    }

    // Update profile if requested
    if (updateProfile) {
      try {
        // Update auth user metadata
        const { error: authError } = await supabase.auth.updateUser({
          data: { timezone: detectedTimezone }
        })

        if (authError && !silentMode) {
          console.error('Error updating user auth metadata with timezone:', authError)
        }

        // Update profile table
        const { error: profileError } = await supabase
          .schema('marketing_and_customer_success')
          .from('profiles')
          .upsert({
            id: user.id,
            timezone: detectedTimezone,
            updated_at: new Date().toISOString()
          })

        if (profileError && !silentMode) {
          console.error('Error updating user profile with timezone:', profileError)
        }

        if (!silentMode) {
          console.log('Auto-detected and set user timezone:', detectedTimezone)
        }
      } catch (error) {
        if (!silentMode) {
          console.error('Error saving auto-detected timezone:', error)
        }
        return {
          detected: true,
          timezone: detectedTimezone,
          wasAlreadySet: false,
          error: 'Detected timezone but failed to save it'
        }
      }
    }

    return {
      detected: true,
      timezone: detectedTimezone,
      wasAlreadySet: false
    }

  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error'
    if (!silentMode) {
      console.error('Error in autoDetectUserTimezone:', error)
    }
    return {
      detected: false,
      timezone: null,
      wasAlreadySet: false,
      error: errorMessage
    }
  }
}

/**
 * Check if a user needs timezone auto-detection
 */
export async function shouldAutoDetectTimezone(user: User): Promise<boolean> {
  try {
    // Check if user already has a timezone set in metadata
    const existingTimezone = user.user_metadata?.timezone || user.app_metadata?.timezone
    if (existingTimezone && isValidTimezone(existingTimezone)) {
      return false
    }

    // Check if user has timezone in profile
    const { data: profile } = await supabase
      .schema('marketing_and_customer_success')
      .from('profiles')
      .select('timezone')
      .eq('id', user.id)
      .single()

    if (profile?.timezone && isValidTimezone(profile.timezone)) {
      return false
    }

    return true
  } catch (error) {
    console.error('Error checking if timezone auto-detection is needed:', error)
    return false
  }
}

/**
 * Show a user-friendly notification about timezone auto-detection
 */
export function createTimezoneDetectionNotification(
  detectionResult: TimezoneAutoDetectionResult,
  onChangeTimezone?: () => void
): {
  title: string
  description: string
  actions?: Array<{ label: string; action: () => void }>
} | null {
  if (!detectionResult.detected || !detectionResult.timezone) {
    return null
  }

  const timezoneDisplayName = detectionResult.timezone
    .split('/')
    .pop()
    ?.replace(/_/g, ' ') || detectionResult.timezone

  return {
    title: 'Timezone Auto-Detected',
    description: `We've automatically set your timezone to ${timezoneDisplayName}. You can change this in your profile settings if needed.`,
    actions: onChangeTimezone ? [
      {
        label: 'Change Timezone',
        action: onChangeTimezone
      }
    ] : undefined
  }
}

export default {
  autoDetectUserTimezone,
  shouldAutoDetectTimezone,
  createTimezoneDetectionNotification
}